import React, { useState } from 'react';
import './SanMarSearch.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

const SanMarSearch = ({ onSelectProduct, onCancel }) => {
  const [styleNumber, setStyleNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!styleNumber.trim()) {
      setError('Please enter a style number');
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResult(null);
    setSelectedColors([]);

    try {
      const response = await fetch(
        `${API_BASE}/sanmar/product/${encodeURIComponent(styleNumber.trim().toUpperCase())}`,
        { credentials: 'include' }
      );

      if (response.status === 404) {
        setError(`No products found for style "${styleNumber.toUpperCase()}"`);
        return;
      }

      if (response.status === 503) {
        setError('SanMar service is not configured. Please contact administrator.');
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to search SanMar');
      }

      const data = await response.json();
      setSearchResult(data.product);

      // Pre-select all colors by default
      if (data.product?.allColors) {
        setSelectedColors(data.product.allColors);
      }
    } catch (err) {
      console.error('SanMar search error:', err);
      setError(err.message || 'Failed to search SanMar catalog');
    } finally {
      setLoading(false);
    }
  };

  const toggleColor = (color) => {
    setSelectedColors(prev =>
      prev.includes(color)
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const selectAllColors = () => {
    if (searchResult?.allColors) {
      setSelectedColors([...searchResult.allColors]);
    }
  };

  const deselectAllColors = () => {
    setSelectedColors([]);
  };

  const handleUseProduct = () => {
    if (!searchResult) return;

    // Build color_images only for selected colors
    const filteredColorImages = {};
    for (const color of selectedColors) {
      if (searchResult.color_images?.[color]) {
        filteredColorImages[color] = searchResult.color_images[color];
      }
    }

    // Prepare product data for the form
    const productData = {
      name: searchResult.name || '',
      price: searchResult.price || '',
      sizes: searchResult.sizes || '',
      colors: selectedColors.join(', '),
      image_url: searchResult.image_url || '',
      color_images: filteredColorImages,
      sanmar_style: searchResult.style,
      sanmar_data: {
        style: searchResult.style,
        brandName: searchResult.brandName,
        description: searchResult.description,
        pricing: searchResult.pricing,
        allColors: searchResult.allColors,
        allSizes: searchResult.allSizes,
        fetchedAt: new Date().toISOString()
      }
    };

    onSelectProduct(productData);
  };

  return (
    <div className="sanmar-search">
      <div className="sanmar-search-header">
        <h3>Search SanMar Catalog</h3>
        <p className="sanmar-search-hint">
          Enter a SanMar style number (e.g., PC61, G500, DT6000) to auto-populate product details.
        </p>
      </div>

      <form onSubmit={handleSearch} className="sanmar-search-form">
        <div className="sanmar-search-input-group">
          <input
            type="text"
            value={styleNumber}
            onChange={(e) => setStyleNumber(e.target.value.toUpperCase())}
            placeholder="Enter style number..."
            className="sanmar-search-input"
            autoFocus
          />
          <button
            type="submit"
            className="sanmar-search-btn"
            disabled={loading || !styleNumber.trim()}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {error && (
        <div className="sanmar-error">
          {error}
        </div>
      )}

      {searchResult && (
        <div className="sanmar-result">
          <div className="sanmar-result-header">
            <div className="sanmar-result-image">
              {searchResult.image_url ? (
                <img src={searchResult.image_url} alt={searchResult.name} />
              ) : (
                <div className="sanmar-no-image">No Image</div>
              )}
            </div>
            <div className="sanmar-result-info">
              <h4>{searchResult.name}</h4>
              <div className="sanmar-result-meta">
                <span className="sanmar-style-badge">{searchResult.style}</span>
                {searchResult.brandName && (
                  <span className="sanmar-brand">{searchResult.brandName}</span>
                )}
              </div>
              {searchResult.price && (
                <div className="sanmar-price">
                  Starting at <strong>${searchResult.price}</strong>
                </div>
              )}
              <div className="sanmar-sizes">
                <strong>Sizes:</strong> {searchResult.sizes}
              </div>
            </div>
          </div>

          {searchResult.allColors && searchResult.allColors.length > 0 && (
            <div className="sanmar-colors-section">
              <div className="sanmar-colors-header">
                <strong>Select Colors ({selectedColors.length} of {searchResult.allColors.length}):</strong>
                <div className="sanmar-colors-actions">
                  <button
                    type="button"
                    className="sanmar-link-btn"
                    onClick={selectAllColors}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="sanmar-link-btn"
                    onClick={deselectAllColors}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="sanmar-colors-grid">
                {searchResult.allColors.map((color) => (
                  <label key={color} className="sanmar-color-option">
                    <input
                      type="checkbox"
                      checked={selectedColors.includes(color)}
                      onChange={() => toggleColor(color)}
                    />
                    <span className="sanmar-color-chip">
                      {searchResult.color_images?.[color] && (
                        <img
                          src={searchResult.color_images[color]}
                          alt={color}
                          className="sanmar-color-thumb"
                        />
                      )}
                      <span className="sanmar-color-name">{color}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="sanmar-result-actions">
            <button
              type="button"
              className="se-btn se-btn-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="se-btn se-btn-primary"
              onClick={handleUseProduct}
              disabled={selectedColors.length === 0}
            >
              Use This Product
            </button>
          </div>
        </div>
      )}

      {!searchResult && !loading && !error && (
        <div className="sanmar-search-footer">
          <button
            type="button"
            className="sanmar-link-btn"
            onClick={onCancel}
          >
            Cancel and enter manually
          </button>
        </div>
      )}
    </div>
  );
};

export default SanMarSearch;
